import React, { useState, useMemo } from 'react';
import PropTypes from 'prop-types';
import USBTable from '@usb-shield/react-table';
import { getCoreRowModel, getSortedRowModel } from '@tanstack/react-table';
import { 
  DATA_TYPES, 
  SORT_DIRECTION, 
  DEFAULT_PAGE_SIZE, 
  DEFAULT_PAGE_SIZE_OPTIONS 
} from './tableTypes';

/**
 * ShieldTable Component
 * A wrapper around @usb-shield/react-table v3 for consistent table usage
 */
const ShieldTable = ({
  data = [],
  columns = [],
  defaultSort = null,
  pagination = {},
  enablePagination = true,
  emptyMessage = 'No data available',
  loading = false,
  className = '',
  striped = true,
  tableId = 'shield-table',
  caption = '',
  stickyHeader = false,
}) => {
  // Pagination state
  const {
    pageSize: initialPageSize = DEFAULT_PAGE_SIZE,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    showPageSizeSelector = true,
    showPageInfo = true
  } = pagination;

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(initialPageSize);

  // Sorting state for tanstack table
  const [sorting, setSorting] = useState(
    defaultSort ? [{ id: defaultSort.key, desc: defaultSort.direction === 'desc' }] : []
  );

  // Paginated data
  const paginatedData = useMemo(() => {
    if (!enablePagination) {
      return data;
    }
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return data.slice(startIndex, endIndex);
  }, [data, currentPage, pageSize, enablePagination]);

  // Total pages calculation
  const totalPages = useMemo(() => {
    return Math.ceil(data.length / pageSize);
  }, [data.length, pageSize]);

  // Handle page change
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
  };

  // Handle page size change
  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };

  // Transform columns to Shield table v3 format
  const shieldColumns = useMemo(() => {
    return columns.map(col => {
      const columnDef = {
        id: col.key,
        header: col.headerName || col.header || col.key,
        accessorKey: col.key,
        enableSorting: col.sortable !== false,
      };

      // Add width if specified
      if (col.width) {
        columnDef.size = typeof col.width === 'number' ? col.width : parseInt(col.width, 10) || 150;
      }
      if (col.minWidth) {
        columnDef.minSize = typeof col.minWidth === 'number' ? col.minWidth : parseInt(col.minWidth, 10) || 50;
      }

      // Add custom cell renderer if provided
      if (col.render) {
        columnDef.cell = (info) => {
          const value = info.getValue();
          const row = info.row.original;
          return col.render(value, row, info.row.index);
        };
      }

      return columnDef;
    });
  }, [columns]);

  // Shield Table v3 options object with sorting
  const tableOptions = useMemo(() => ({
    data: paginatedData,
    columns: shieldColumns,
    state: {
      sorting,
    },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  }), [paginatedData, shieldColumns, sorting]);

  // Handle empty state
  if (!loading && data.length === 0) {
    return (
      <div className={`shield-table-container ${className}`}>
        <div className="shield-table-empty" role="status" style={{ padding: '20px', textAlign: 'center' }}>
          {emptyMessage}
        </div>
      </div>
    );
  }

  // Loading state
  if (loading) {
    return (
      <div className={`shield-table-container ${className}`}>
        <div className="shield-table-loading" role="status" aria-busy="true" style={{ padding: '20px', textAlign: 'center' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className={`shield-table-container ${className}`} style={{ width: '100%', overflowX: 'auto' }}>
      <USBTable
        id={tableId}
        options={tableOptions}
        content={{
          caption: caption || '',
          ariaLabel: caption || 'Data table'
        }}
        isSortable={true}
        isZebraStriped={false}
        responsiveView="scrollable"
      />
      
      {enablePagination && data.length > 0 && (
        <div className="shield-table-pagination" style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center', 
          padding: '12px 0',
          flexWrap: 'wrap',
          gap: '10px'
        }}>
          <div className="pagination-info">
            {showPageInfo && (
              <span>
                Showing {((currentPage - 1) * pageSize) + 1} to{' '}
                {Math.min(currentPage * pageSize, data.length)} of{' '}
                {data.length} entries
              </span>
            )}
          </div>
          
          <div className="pagination-controls" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <button
              type="button"
              className="pagination-btn"
              onClick={() => handlePageChange(1)}
              disabled={currentPage === 1}
              aria-label="Go to first page"
              style={{ padding: '6px 12px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              First
            </button>
            <button
              type="button"
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 1}
              aria-label="Go to previous page"
              style={{ padding: '6px 12px', cursor: currentPage === 1 ? 'not-allowed' : 'pointer' }}
            >
              Previous
            </button>
            
            <span className="pagination-current" style={{ padding: '0 10px' }}>
              Page {currentPage} of {totalPages}
            </span>
            
            <button
              type="button"
              className="pagination-btn"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage === totalPages}
              aria-label="Go to next page"
              style={{ padding: '6px 12px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
            >
              Next
            </button>
            <button
              type="button"
              className="pagination-btn"
              onClick={() => handlePageChange(totalPages)}
              disabled={currentPage === totalPages}
              aria-label="Go to last page"
              style={{ padding: '6px 12px', cursor: currentPage === totalPages ? 'not-allowed' : 'pointer' }}
            >
              Last
            </button>
          </div>

          {showPageSizeSelector && (
            <div className="pagination-size-selector" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label htmlFor={`${tableId}-page-size`}>
                Rows per page:
              </label>
              <select
                id={`${tableId}-page-size`}
                value={pageSize}
                onChange={(e) => handlePageSizeChange(Number(e.target.value))}
                style={{ padding: '4px 8px' }}
              >
                {pageSizeOptions.map(size => (
                  <option key={size} value={size}>{size}</option>
                ))}
              </select>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

ShieldTable.propTypes = {
  data: PropTypes.arrayOf(PropTypes.object),
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      key: PropTypes.string.isRequired,
      headerName: PropTypes.string,
      header: PropTypes.string,
      dataType: PropTypes.oneOf(Object.values(DATA_TYPES)),
      sortable: PropTypes.bool,
      render: PropTypes.func,
      width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      minWidth: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    })
  ),
  defaultSort: PropTypes.shape({
    key: PropTypes.string.isRequired,
    direction: PropTypes.oneOf(Object.values(SORT_DIRECTION))
  }),
  pagination: PropTypes.shape({
    pageSize: PropTypes.number,
    pageSizeOptions: PropTypes.arrayOf(PropTypes.number),
    showPageSizeSelector: PropTypes.bool,
    showPageInfo: PropTypes.bool
  }),
  enablePagination: PropTypes.bool,
  emptyMessage: PropTypes.string,
  loading: PropTypes.bool,
  className: PropTypes.string,
  striped: PropTypes.bool,
  tableId: PropTypes.string,
  caption: PropTypes.string,
  stickyHeader: PropTypes.bool,
};

export default ShieldTable;
